<template>
  <div>
    <b-overlay
      :show="!dataIsReady"
      spinner-type="border"
      variant="light"
      spinner-variant="primary"
      opacity="1"
      rounded>
      <failed-data-get
        v-if="FailedDataGetStatus"
        text="Looks like this server doesn't exist." />

      <template v-else>
        <div class="d-flex align-items-end pt-2 pb-6">
          <div class="d-flex flex-column">
            <h1 class="h5 mb-2">
              VPN Servers of «{{ selectedServer.name }}»
            </h1>
            <b-breadcrumb class="breadcrumb-transparent breadcrumb-line p-0 m-0">
              <b-breadcrumb-item :to="{ name: 'Dashboard' }">
                <i class="fa fa-home fa-sm mr-2"></i>
                Home
              </b-breadcrumb-item>
              <b-breadcrumb-item :to="{ name: 'ServersList'}">Servers</b-breadcrumb-item>
              <b-breadcrumb-item active>VPN Servers</b-breadcrumb-item>
            </b-breadcrumb>
          </div>
        </div>

        <b-row>
          <b-col xl="9">
            <b-card
              no-body
              class="card-custom card-border"
              style="min-height: 100px;">
              <b-card-body v-show="dataIsReady">
                <b-table
                  :items="vpnServersList"
                  :fields="fields"
                  :class="{ 'table-hover' : !vpnServersEmpty }"
                  stacked="md"
                  head-variant=""
                  show-empty
                  responsive
                  class="mb-0">
                  <template #cell(order)="data">
                    <span class="small font-weight-light">{{ data.index + 1 }}</span>
                  </template>
                  <template #cell(name)="data">
                    <div class="d-flex align-items-center">
                      <span>{{ data.item.name }}</span>
                      <span class="svg-icon svg-icon-sm ml-3">
                        <inline-svg
                          :src="`media/svg/icons/VPN/${data.item.code}.svg`"
                          class="svg-icon m-0" />
                      </span>
                    </div>
                  </template>
                  <template #cell(active)="data">
                    <b-badge :variant="vpnServerInstallBadge(data.item).variant">
                      <i
                        :class="[
                          `fa-${vpnServerInstallBadge(data.item).icon}`,
                          `text-${vpnServerInstallBadge(data.item).iconColor}`
                        ]"
                        class="fa fa-sm"></i>
                      {{ vpnServerInstallBadge(data.item).text }}
                    </b-badge>
                  </template>
                  <template #cell(edit)="data">
                    <div class="d-flex align-items-center flex-wrap flex-sm-nowrap">
                      <b-button-group class="mr-6 mb-2 mb-sm-0 ml-auto">
                        <b-button
                          v-if="data.item.installed"
                          v-wave
                          size="sm"
                          variant="outline-primary"
                          :disabled="serversResponses('getReinstallVpnServerParams')"
                          class="align-middle"
                          @click="showModal('reinstallVpnServer', { vpn_code: data.item.code })">
                          <i class="fa fa-redo fa-sm"></i>
                          <span>Reinstall</span>
                        </b-button>
                        <b-button
                          :id="`deleteButton-${data.item.code}`"
                          v-wave
                          size="sm"
                          variant="outline-primary"
                          class="align-middle"
                          :disabled="data.item.pending"
                          @click="handleVpnServerInstall(data.item.installed, data.item.code)">
                          <i
                            class="fa fa-ban fa-sm"
                            :class="data.item.installed ? 'fa-ban' : 'fa-download'"></i>
                          <span>{{ data.item.installed ? 'Remove' : 'Install' }}</span>
                        </b-button>
                      </b-button-group>
                      <b-button
                        size="sm"
                        v-wave
                        variant="outline-primary"
                        class="align-middle"
                        :disabled="vpnServerParamsLength(data.item.code) == 0 || serversResponses('getVpnServerParams')"
                        @click="showModal('vpnServerParams', { vpn_code: data.item.code })">
                        <i class="fa fa-list fa-sm"></i>
                        <span>Show Parameters ({{ vpnServerParamsLength(data.item.code) }})</span>
                      </b-button>
                    </div>
                  </template>
                  <template #empty="scope">
                    <no-data-found />
                  </template>
                </b-table>
              </b-card-body>
            </b-card>
          </b-col>
          <b-col xl="3">
            <server-details-plate custom-item="vpn_installed" />
          </b-col>
        </b-row>
      </template>
    </b-overlay>

    <b-popover
      v-for="server in vpnServersList"
      :key="`popover-${server.code}`"
      :target="`deleteButton-${server.code}`"
      :ref="`popover-${server.code}`"
      triggers="manual blur"
      placement="left">
      <p>By confirming this action, a request to delete the VPN server will be sent, resetting all connected clients. Are you sure?</p>
      <div class="d-flex w-100">
        <b-button
          @click="hideDeletePopover(server.code)"
          variant="outline-primary"
          size="sm">
          Cancel
        </b-button>
        <b-button
          variant="danger"
          size="sm"
          class="ml-4 text-center justify-content-center flex-grow-1"
          @click="manageVpnServerStatus('uninstall', server.code), hideDeletePopover(server.code)">
          <i class="fa fa-check fa-sm"></i>
          <span>Yes, remove</span>
        </b-button>
      </div>
    </b-popover>

    <modal-reinstall-vpn-server />
    <modal-vpn-server-params />
  </div>
</template>

<script>
import {
  // actions
  GET_SERVER,
  MANAGE_VPN_SERVER_STATUS,
  GET_REINSTALL_VPN_SERVER_PARAMS,
  GET_VPN_SERVER_PARAMS,

  // mutations
  SELECT_SERVER,
  UNSELECT_SERVER,
  SET_FAILED_SERVER_GET,
} from '@/core/services/store/servers.module'
import {
  // actions
  GET_VPN_SERVERS,
  CHECK_VPN_SERVER_MANAGE_STATUS,

  // mutations
  SELECT_VPN_SERVER,
  UNSELECT_VPN_SERVER,
  CLEAR_VPN_SERVERS
} from '@/core/services/store/vpn-servers.module'
import { GET_COUNTRIES } from '@/core/services/store/countries.module'
import { mapGetters } from 'vuex'
import { eventBus } from '@/main'
import {
  showDeletePopover,
  hideDeletePopover,
  showToast,
} from '@/core/utils'
import ModalReinstallVpnServer from './components/ModalReinstallVpnServer.vue'
import ModalVpnServerParams from './components/ModalVpnServerParams.vue'
import ServerDetailsPlate from './components/ServerDetailsPlate.vue'

const ACTIVE_INTERVAL   = 6000    // 6 sec
const UNACTIVE_INTERVAL = 300000  // 5 min

export default {
  name: 'VpnServers',
  components: {
    ModalReinstallVpnServer,
    ModalVpnServerParams,
    ServerDetailsPlate,
  },
  data() {
    return {
      dataIsReady: false,

      fields: [
        {
          label: '#',
          key: 'order',
          class: ['align-middle', 'pl-5'],
          thStyle: 'width: 50px;'
        },
        {
          label: 'Name',
          key: 'name',
          class: ['align-middle', 'font-weight-bold', 'text-nowrap', 'pl-5'],
          thStyle: 'width: 20%;'
        },
        {
          label: 'Code',
          key: 'code',
          class: ['align-middle', 'text-nowrap'],
          thStyle: 'width: 20%;'
        },
        {
          label: 'Status',
          key: 'active',
          class: ['align-middle', 'text-nowrap', 'font-size-sm'],
          thStyle: 'width: 250px;',
        },
        {
          label: 'Actions',
          key: 'edit',
          class: ['align-middle', 'text-md-right', 'text-nowrap', 'pr-5', 'pl-10'],
        }
      ],

      checkInterval: ACTIVE_INTERVAL,
      checkTimer: null,

      showDeletePopover,
      hideDeletePopover,
      showToast,
    }
  },
  computed: {
    ...mapGetters([
      'countryFlag',
      'countryName',

      'selectedServer',
      'selectedServerId',
      'FailedDataGetStatus',
      'serverStatusBadge',
      'serversResponses',

      'vpnServersList',
      'vpnServersEmpty',
      'selectedVpnServer',
      'totalVpnServers',
      'vpnServerParamsLength',
      'vpnServerInstallBadge',
      'selectedVpnServerExist',
      'someVpnServerPending',
      'noneVpnServerPending',
      'installedVpnServersCount',
      'vpnServersResponses'
    ]),

    routeId() {
      return this.$route.params.id
    }
  },
  watch: {
    /* eslint-disable-next-line no-unused-vars */
    routeId(newVal, oldVal) {
      if (newVal) {
        this.dataIsReady = false
        this.$store.commit(UNSELECT_SERVER)
        this.getServer(newVal)
      }
    },

    /* eslint-disable-next-line no-unused-vars */
    someVpnServerPending(newVal, oldVal) {
      if (newVal) {
        this.setCheckInterval()
      } else {
        this.clearCheckTimer()
      }
    },

    /* eslint-disable-next-line no-unused-vars */
    noneVpnServerPending(newVal, oldVal) {
      if (newVal) {
        this.clearCheckTimer()
      }
    },
  },
  created() {
    this.$store.dispatch(GET_COUNTRIES)
    this.$store.commit(SET_FAILED_SERVER_GET, false)

    this.getServer(this.routeId)

    eventBus.$on('showToast/VpnServers', ({ message, variant }) => {
      this.showToast(message, variant)
    })
  },
  mounted() {
    const that = this

    document.addEventListener('visibilitychange', () => {
      that.clearCheckTimer()
      that.checkInterval = document.hidden ? UNACTIVE_INTERVAL : ACTIVE_INTERVAL
      that.setCheckInterval()
    })
  },
  beforeDestroy() {
    this.clearCheckTimer()
    this.dataIsReady = false
    document.removeEventListener('visibilitychange')
  },
  destroyed() {
    eventBus.$off('showToast/VpnServers')

    this.$store.commit(UNSELECT_SERVER)
    this.$store.commit(SET_FAILED_SERVER_GET, false)
    this.$store.commit(CLEAR_VPN_SERVERS)
  },
  methods: {
    responseReady(data) {
      let result = []
      for (let i = 0; i < data.length; i++) {
        result.push(Array.isArray(data[i]) || typeof data == 'object')
      }
      return result.every(el => el)
    },

    showModal(type, { vpn_code }) {
      let requirements = {
        reinstallVpnServer: [
          () => this.$store.dispatch(GET_REINSTALL_VPN_SERVER_PARAMS, {
            vpn_code,
            server_id: this.selectedServerId
          }).then(server => {
            this.selectVpnServer(server)
          })
        ],
        vpnServerParams: [
          () => this.$store.dispatch(GET_VPN_SERVER_PARAMS, {
            vpn_code,
            server_id: this.selectedServerId
          }).then(server => {
            this.selectVpnServer(server)
          })
        ]
      }

      if (requirements.hasOwnProperty(type)) {
        Promise
          .all(requirements[type].map(promiseFn => promiseFn()))
          .then(res => {
            if (this.responseReady(res)) {
              eventBus.$emit('openModal', type)
            }
          })
      } else {
        eventBus.$emit('openModal', type)
      }
    },

    setCheckInterval() {
      this.checkTimer = setInterval(() => {
        if (this.someVpnServerPending) {
          this.checkVpnServerManageStatus(this.routeId)
        }
      }, this.checkInterval)
    },

    clearCheckTimer() {
      clearInterval(this.checkTimer)
    },

    checkVpnServerManageStatus(id) {
      this.$store.dispatch(CHECK_VPN_SERVER_MANAGE_STATUS, { server_id: id })
    },

    getServer(id) {
      this.$store
        .dispatch(GET_SERVER, { id })
        .then(data => {
          this.$store.commit(SELECT_SERVER, data)
        })
        .finally(() => { this.getVpnServers(id) })
    },

    getVpnServers(id) {
      this.$store
        .dispatch(GET_VPN_SERVERS, { server_id: id })
        .finally(() => { this.dataIsReady = true })
    },

    handleVpnServerInstall(installed, vpn_code) {
      if (installed) {
        this.showDeletePopover(vpn_code)
      } else {
        this.manageVpnServerStatus('install', vpn_code)
      }
    },

    manageVpnServerStatus(action, vpn_code) {
      this.$store.dispatch(MANAGE_VPN_SERVER_STATUS, { action, vpn_code })
    },

    getReinstallVpnServerParams(action, vpn_code) {
      this.$store.dispatch(GET_REINSTALL_VPN_SERVER_PARAMS, {
        action,
        vpn_code,
        server_id: this.selectedServerId
      })
    },

    selectVpnServer(vpnServer) {
      this.$store.commit(SELECT_VPN_SERVER, vpnServer)
    },

    unselectVpnServer() {
      this.$store.commit(UNSELECT_VPN_SERVER)
    },
  },
}
</script>
